import {
  Badge,
  Box,
  Code,
  HStack,
  Icon,
  Input,
  Text,
  Textarea,
  VStack
} from "@chakra-ui/react";
import React from "react";
import { FiCode, FiExternalLink, FiInfo, FiPlus } from "react-icons/fi";

interface AdvancedSettingsProps {
  metadata: string;
  metadataError: string | null;
  forceExternalBrowser: boolean;
  customQueryParams: string;
  redirectUrl: string;
  onMetadataChange: (value: string, error: string | null) => void;
  onForceExternalBrowserChange: (value: boolean) => void;
  onCustomQueryParamsChange: (value: string) => void;
  disabled?: boolean;
}

export const AdvancedSettings: React.FC<AdvancedSettingsProps> = ({
  metadata,
  metadataError,
  forceExternalBrowser,
  customQueryParams,
  redirectUrl,
  onMetadataChange,
  onForceExternalBrowserChange,
  onCustomQueryParamsChange,
  disabled = false
}) => {
  const handleMetadataChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    let error = null;

    if (value) {
      try {
        JSON.parse(value);
      } catch {
        error = "Invalid JSON format";
      }
    }

    onMetadataChange(value, error);
  };

  const handleCustomQueryParamsChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    onCustomQueryParamsChange(e.target.value);
  };

  return (
    <Box bg="white" shadow="sm" rounded="lg" p={6}>
      <HStack mb={4}>
        <Icon as={FiCode} color="blue.500" boxSize={5} />
        <Text fontWeight="semibold" fontSize="lg">
          Advanced Settings
        </Text>
      </HStack>

      <VStack gap={4} align="stretch">
        <Box>
          <HStack mb={2}>
            <Text fontWeight="medium">Page Metadata (JSON)</Text>
            <Badge colorScheme="gray" size="sm">
              Optional
            </Badge>
          </HStack>
          <Textarea
            name="metadata"
            value={metadata}
            onChange={handleMetadataChange}
            placeholder={`{
  "title": "Special Offer - Summer Sale",
  "description": "Get 50% off all items",
  "customConfig": {
    "showBanner": true,
    "theme": "summer"
  }
}`}
            size="md"
            rows={8}
            fontFamily="mono"
            fontSize="sm"
            disabled={disabled}
            borderColor={metadataError ? "red.500" : undefined}
            _focus={{
              borderColor: metadataError ? "red.500" : "blue.500",
              boxShadow: `0 0 0 1px var(--chakra-colors-${
                metadataError ? "red" : "blue"
              }-500)`
            }}
          />
          {metadataError && (
            <Text fontSize="sm" color="red.500" mt={1}>
              {metadataError}
            </Text>
          )}
          <Text fontSize="sm" color="gray.500" mt={1}>
            This JSON will be injected as{" "}
            <Code fontSize="xs">window._metadata</Code> on the landing page. Use
            it to customize page behavior, titles, or pass configuration data.
          </Text>
        </Box>

        <Box>
          <HStack mb={2}>
            <Icon as={FiExternalLink} color="gray.500" boxSize={4} />
            <Text fontWeight="medium">Force External Browser</Text>
            <Badge colorScheme="purple" size="sm">
              Recommended for Meta Ads
            </Badge>
          </HStack>
          <Box
            p={4}
            bg="gray.50"
            rounded="md"
            border="1px"
            borderColor="gray.200"
          >
            <HStack justify="space-between" align="center">
              <VStack align="start" gap={1} flex={1}>
                <Text fontSize="sm" fontWeight="medium">
                  Open in Safari/Chrome
                </Text>
                <Text fontSize="xs" color="gray.600">
                  Forces the link to open in the user's default browser instead
                  of Meta's embedded browser (Facebook, Instagram)
                </Text>
              </VStack>
              <Box
                position="relative"
                display="inline-block"
                width="50px"
                height="28px"
                bg={forceExternalBrowser ? "purple.500" : "gray.300"}
                borderRadius="full"
                transition="all 0.3s"
                cursor={disabled ? "not-allowed" : "pointer"}
                opacity={disabled ? 0.6 : 1}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!disabled) {
                    onForceExternalBrowserChange(!forceExternalBrowser);
                  }
                }}
                _hover={{
                  bg: forceExternalBrowser ? "purple.600" : "gray.400"
                }}
                _after={{
                  content: '""',
                  position: "absolute",
                  width: "24px",
                  height: "24px",
                  borderRadius: "full",
                  bg: "white",
                  top: "2px",
                  left: forceExternalBrowser ? "24px" : "2px",
                  transition: "left 0.3s",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
                }}
              />
            </HStack>
            {forceExternalBrowser && (
              <Box
                mt={3}
                p={3}
                bg="purple.50"
                rounded="md"
                borderLeft="3px solid"
                borderColor="purple.400"
              >
                <HStack gap={2} align="start">
                  <Icon as={FiInfo} color="purple.600" boxSize={4} mt={0.5} />
                  <VStack align="start" gap={1}>
                    <Text fontSize="xs" color="purple.800" fontWeight="medium">
                      How it works:
                    </Text>
                    <Text fontSize="xs" color="purple.700">
                      • Detects if the user is in Meta's in-app browser
                    </Text>
                    <Text fontSize="xs" color="purple.700">
                      • Improves tracking accuracy and user experience
                    </Text>
                    <Text fontSize="xs" color="purple.700">
                      • Helps bypass Meta browser restrictions
                    </Text>
                  </VStack>
                </HStack>
              </Box>
            )}
          </Box>
        </Box>

        <Box>
          <HStack mb={2}>
            <Icon as={FiPlus} color="gray.500" boxSize={4} />
            <Text fontWeight="medium">Custom Query Parameters</Text>
            <Badge colorScheme="blue" size="sm">
              Optional
            </Badge>
          </HStack>
          <Box>
            <Input
              name="customQueryParams"
              value={customQueryParams}
              onChange={handleCustomQueryParamsChange}
              placeholder="utm_source=facebook&utm_medium=cpc&utm_campaign=summer2024"
              size="lg"
              fontFamily="mono"
              fontSize="sm"
              disabled={disabled}
              _focus={{
                borderColor: "blue.500",
                boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)"
              }}
            />
            <Text fontSize="sm" color="gray.500" mt={2}>
              Add custom query parameters to append to all destination URLs.
              Enter without "?" prefix (e.g.,
              utm_source=fb&utm_campaign=summer). URL parameters from the
              tracking link are always passed through automatically.
            </Text>
            {customQueryParams && (
              <Box
                mt={3}
                p={3}
                bg="blue.50"
                rounded="md"
                border="1px"
                borderColor="blue.200"
              >
                <Text fontSize="sm" color="blue.900" fontWeight="medium" mb={1}>
                  Preview:
                </Text>
                <Code
                  fontSize="xs"
                  p={2}
                  bg="white"
                  rounded="md"
                  display="block"
                  wordBreak="break-all"
                >
                  {redirectUrl
                    ? `${redirectUrl}${
                        redirectUrl.includes("?") ? "&" : "?"
                      }${customQueryParams}`
                    : `[destination]?${customQueryParams}`}
                </Code>
                <Text fontSize="xs" color="blue.700" mt={2}>
                  <strong>Note:</strong> These parameters will be added to every
                  redirect, in addition to any parameters passed from the
                  tracking link.
                </Text>
              </Box>
            )}
          </Box>
        </Box>
      </VStack>
    </Box>
  );
};
